import React, { useState, useEffect } from 'react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, addDays } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import BottomNav from '../components/BottomNav';
import { FileSpreadsheet, Calendar, Download } from 'lucide-react';

export default function ReportsPage() {
  const { user } = useAuth();
  
  const [masters, setMasters] = useState([]);
  const [selectedInstructor, setSelectedInstructor] = useState('');
  const [periodType, setPeriodType] = useState('month'); // day, week, month, custom
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  
  // Загрузка списка инструкторов
  useEffect(() => {
    fetch('/api/mastercars')
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        setMasters(data);
        if (data.length > 0) {
          setSelectedInstructor(data[0].id);
        }
      })
      .catch(err => console.error('Ошибка загрузки инструкторов:', err));
  }, []);
  
  // Установка периода при изменении типа
  useEffect(() => {
    const today = new Date();
    
    switch (periodType) {
      case 'day':
        setFromDate(format(today, 'yyyy-MM-dd'));
        setToDate(format(today, 'yyyy-MM-dd'));
        break;
      case 'week':
        setFromDate(format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'));
        setToDate(format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'));
        break;
      case 'month':
        setFromDate(format(startOfMonth(today), 'yyyy-MM-dd'));
        setToDate(format(endOfMonth(today), 'yyyy-MM-dd'));
        break;
      case 'custom':
        // Оставляем текущие значения
        break;
    }
  }, [periodType]);
  
  // Загрузка данных отчёта
  const loadReport = async () => {
    if (!selectedInstructor || !fromDate || !toDate) {
      setMessage('Выберите инструктора и период');
      return;
    }
    
    setLoading(true);
    setMessage('');
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `/api/reports/instructor/${selectedInstructor}?from=${fromDate}&to=${toDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        setReportData(data);
      } else {
        const errData = await res.json();
        setMessage(errData.message || 'Ошибка загрузки отчёта');
      }
    } catch (err) {
      console.error('Ошибка загрузки отчёта:', err);
      setMessage('Ошибка соединения с сервером');
    } finally {
      setLoading(false);
    }
  };
  
  // Скачивание Excel
  const downloadExcel = async () => {
    if (!selectedInstructor || !fromDate || !toDate) {
      setMessage('Выберите инструктора и период');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `/api/reports/instructor/${selectedInstructor}/excel?from=${fromDate}&to=${toDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `report_${selectedInstructor}_${fromDate}_${toDate}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        setMessage('✅ Отчёт скачан');
      } else {
        const errData = await res.json();
        setMessage(errData.message || 'Ошибка скачивания');
      }
    } catch (err) {
      console.error('Ошибка скачивания:', err);
      setMessage('Ошибка соединения с сервером');
    }
  };
  
  if (user?.status !== 'staff') {
    return <div className="text-red-500 text-center mt-20 text-2xl">Доступ запрещён</div>;
  }
  
  return (
    <div className="min-h-screen bg-[#1b1b1b] text-[#dedede] pb-20">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 pt-24">
        <div className="mb-6 flex items-center gap-3">
          <FileSpreadsheet className="text-[#b8975a]" size={32} />
          <div>
            <h1 className="text-2xl font-bold text-[#dedede]">Отчётность</h1>
            <p className="text-sm text-gray-400">Отчёты по занятиям инструкторов</p>
          </div>
        </div>
        
        {/* Панель управления */}
        <div className="bg-[#252525] rounded-lg border border-white/10 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Выбор инструктора */}
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wide">
                Инструктор
              </label>
              <select
                value={selectedInstructor}
                onChange={(e) => setSelectedInstructor(e.target.value)}
                className="w-full p-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-[#b8975a] focus:outline-none"
              >
                {masters.map(m => (
                  <option key={m.id} value={m.id} className="bg-[#1b1b1b]">
                    {m.fullName}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Тип периода */}
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wide">
                Период
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => setPeriodType('day')}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition ${
                    periodType === 'day'
                      ? 'bg-[#b8975a] text-black'
                      : 'bg-white/5 text-slate-300 hover:bg-white/10'
                  }`}
                >
                  День
                </button>
                <button
                  onClick={() => setPeriodType('week')}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition ${
                    periodType === 'week'
                      ? 'bg-[#b8975a] text-black'
                      : 'bg-white/5 text-slate-300 hover:bg-white/10'
                  }`}
                >
                  Неделя
                </button>
                <button
                  onClick={() => setPeriodType('month')}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition ${
                    periodType === 'month'
                      ? 'bg-[#b8975a] text-black'
                      : 'bg-white/5 text-slate-300 hover:bg-white/10'
                  }`}
                >
                  Месяц
                </button>
                <button
                  onClick={() => setPeriodType('custom')}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition ${
                    periodType === 'custom'
                      ? 'bg-[#b8975a] text-black'
                      : 'bg-white/5 text-slate-300 hover:bg-white/10'
                  }`}
                >
                  Свой
                </button>
              </div>
            </div>
          </div>
          
          {/* Даты */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wide">
                От
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                disabled={periodType !== 'custom'}
                className="w-full p-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-[#b8975a] focus:outline-none disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wide">
                До
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                disabled={periodType !== 'custom'}
                className="w-full p-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-[#b8975a] focus:outline-none disabled:opacity-50"
              />
            </div>
          </div>
          
          {/* Кнопки */}
          <div className="flex gap-3">
            <button
              onClick={loadReport}
              disabled={loading}
              className="flex-1 px-4 py-3 bg-[#b8975a] text-black rounded-lg font-medium hover:bg-[#a07d4a] transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Calendar size={18} />
              {loading ? 'Загрузка...' : 'Показать отчёт'}
            </button>
            <button
              onClick={downloadExcel}
              disabled={!reportData}
              className="px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition disabled:opacity-50 flex items-center gap-2"
            >
              <Download size={18} />
              Скачать Excel
            </button>
          </div>
          
          {message && (
            <div className={`mt-4 p-3 rounded-lg text-sm ${
              message.includes('✅') 
                ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                : 'bg-red-500/10 text-red-400 border border-red-500/20'
            }`}>
              {message}
            </div>
          )}
        </div>
        
        {/* Превью отчёта */}
        {reportData && (
          <div className="bg-white rounded-lg shadow-2xl overflow-hidden">
            {/* Заголовок отчёта */}
            <div className="bg-[#E8F0F7] px-6 py-4 border-b border-[#E8EAED]">
              <h2 className="text-xl font-bold text-[#202124]">
                Отчёт по занятиям инструктора
              </h2>
              <p className="text-sm text-[#7A8288] mt-1">
                {reportData.instructor.fullName}
              </p>
              <p className="text-sm text-[#5B8DBE] mt-1">
                Период: {reportData.period.from} — {reportData.period.to}
              </p>
            </div>
            
            {/* Статистика */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-6 bg-[#FAFBFC] border-b border-[#E8EAED]">
              <div className="text-center">
                <div className="text-2xl font-bold text-[#202124]">{reportData.stats.total}</div>
                <div className="text-xs text-[#7A8288] mt-1">Всего занятий</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{reportData.stats.completed}</div>
                <div className="text-xs text-[#7A8288] mt-1">Проведено</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{reportData.stats.noShow}</div>
                <div className="text-xs text-[#7A8288] mt-1">Неявок</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-[#5B8DBE]">{reportData.stats.regular}</div>
                <div className="text-xs text-[#7A8288] mt-1">Обычных</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-[#C97B75]">{reportData.stats.dop}</div>
                <div className="text-xs text-[#7A8288] mt-1">Дополнительных</div>
              </div>
            </div>
            
            {/* Таблица занятий */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#E8F0F7]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#5B8DBE] uppercase">Дата</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#5B8DBE] uppercase">Время</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#5B8DBE] uppercase">Место</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#5B8DBE] uppercase">Ученик</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#5B8DBE] uppercase">Тип</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#5B8DBE] uppercase">Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.lessons.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center text-[#7A8288]">
                        Нет занятий за выбранный период
                      </td>
                    </tr>
                  ) : (
                    reportData.lessons.map((lesson, i) => (
                      <tr key={i} className="border-t border-[#E8EAED] hover:bg-[#FAFBFC]">
                        <td className="px-4 py-3 text-sm text-[#202124]">{lesson.date}</td>
                        <td className="px-4 py-3 text-sm text-[#202124]">{lesson.time}</td>
                        <td className="px-4 py-3 text-sm text-[#202124]">{lesson.place}</td>
                        <td className="px-4 py-3 text-sm text-[#202124]">{lesson.studentName}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            lesson.type === 'dop' 
                              ? 'bg-[#F5E8E6] text-[#C97B75]' 
                              : 'bg-[#E8F0F7] text-[#5B8DBE]'
                          }`}>
                            {lesson.type === 'dop' ? 'Доп.' : 'Обычная'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            lesson.status === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {lesson.status === 'completed' ? '✅ Проведено' : '❌ Неявка'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      
      <BottomNav />
    </div>
  );
}